/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { EnterpriseAdmin_AcceptAllInvitationsForEmailAndOrganizationFromInvitationFailureReason as FailureReason } from '__generated__/graphql-types';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import API from 'js/lib/api';
import { buildHashWithAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import cookie from 'js/lib/cookie';
import redirect from 'js/lib/coursera.redirect';
import user from 'js/lib/user';

import { InlineNotification, LoadingSection } from '@coursera/cds-core';

import { RECRUITER_TEMPLATE_ID } from 'bundles/enterprise-admin-management/constants';
import { EmailVerification } from 'bundles/enterprise-admin-onboarding/components/InviteAcceptance/EmailVerification';
import { InvitationIsNoLongerActive } from 'bundles/enterprise-admin-onboarding/components/InviteAcceptance/InvitationIsNoLongerActive';
import { SwitchAccountForAdminAccess } from 'bundles/enterprise-admin-onboarding/components/InviteAcceptance/SwitchAccountForAdminAccess';
import { YouHaveBeenGrantedAdminPrivileges } from 'bundles/enterprise-admin-onboarding/components/InviteAcceptance/YouHaveBeenGrantedAdminPrivileges';
import type { PropsFromUseInviteAcceptance } from 'bundles/enterprise-admin-onboarding/hooks';
import { Spacer } from 'bundles/enterprise-ui/components';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import { getActionUrl } from 'bundles/user-account/common/lib';
import AuthenticationOption from 'bundles/user-account/components/AuthenticationOption';

import _t from 'i18n!nls/admin-dashboard';

type Props = {
  onAdminRolesUpdated: () => Promise<$TSFixMe>;
} & PropsFromUseInviteAcceptance;
type State = {
  loading: boolean;
  didAccept: boolean;
  errorCode?: FailureReason | 'Not Authorized';
  errorMessage?: string;
};
const DEFAULT_STATE: State = {
  loading: false,
  didAccept: false,
  errorCode: undefined,
  errorMessage: undefined,
};

type PropsFromCaller = {};
export const withInviteAcceptance = (Component: React.ComponentType<PropsFromCaller>) =>
  class InviteAcceptance extends React.Component<Props, State> {
    static contextTypes = {
      router: PropTypes.object.isRequired,
      openModal: PropTypes.func,
      closeModal: PropTypes.func,
    };

    declare context: LegacyContextType<typeof InviteAcceptance.contextTypes>;

    state: State = DEFAULT_STATE;

    handleAcceptedInvite = () => {
      this.props.onAdminRolesUpdated?.().then(() => {
        this.setState({ ...DEFAULT_STATE, didAccept: true });
      });
    };

    handleContinueToCoursera = () => {
      const { router } = this.context;
      const newQuery = { ...router.location.query };
      delete newQuery.adminInvitationId;
      const { thirdPartySlug } = router.params;
      const enableRecruiterInvitationFlow = EnterpriseExperiments.get('enableRecruiterInvitationFlow').includes(
        thirdPartySlug
      );
      if (router.location.query.roleTemplateId === RECRUITER_TEMPLATE_ID && enableRecruiterInvitationFlow) {
        window.location.href = `/recruiting/${thirdPartySlug}/candidates/dashboard`;
      } else {
        router.push({
          ...router.location,
          params: router.params,
          query: newQuery,
        });
      }
    };

    handleInvite = () => {
      const { router } = this.context;
      const { acceptAllInvitationsForEmailAndOrganization, onInviteAccepted } = this.props;
      const { adminInvitationId } = router.location.query;
      this.setState({
        ...DEFAULT_STATE,
        loading: true,
      });

      if (!user.isAuthenticatedUser()) {
        this.setState({ ...DEFAULT_STATE, errorCode: 'Not Authorized' });
        router.push({
          ...router.location,
          params: router.params,
          query: omit({ ...router.location.query }, 'authMode'),
          hash: buildHashWithAuthMode(getLocationHash(router.location), 'signup'),
        });
        return Promise.resolve(undefined);
      }
      return acceptAllInvitationsForEmailAndOrganization({
        variables: { input: { invitationId: adminInvitationId } },
      })
        .then((response) => {
          const result = response.data?.EnterpriseAdmin_AcceptAllInvitationsForEmailAndOrganizationFromInvitation;

          if (
            result?.__typename === 'EnterpriseAdmin_AcceptAllInvitationsForEmailAndOrganizationFromInvitationSuccess'
          ) {
            this.handleAcceptedInvite();
            onInviteAccepted();
          } else if (
            result?.__typename === 'EnterpriseAdmin_AcceptAllInvitationsForEmailAndOrganizationFromInvitationFailure'
          ) {
            this.setState({ ...DEFAULT_STATE, errorCode: result.reason });
            return result.reason;
          }
        })
        .catch(() => {
          this.setState({ ...DEFAULT_STATE, errorMessage: _t('Something went wrong!') });
          return undefined;
        });
    };

    handleLogout = () => {
      const csrfToken = cookie.get('CSRF3-Token');
      const logoutRequest = csrfToken && getActionUrl('logout', csrfToken);
      const api = API('', { type: 'rest' });
      if (logoutRequest) {
        return Promise.resolve(
          api.post(logoutRequest, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          })
        ).then(() => {
          redirect.setLocation(
            `/?redirectTo=${encodeURIComponent(window.location.pathname + window.location.search)}#authMode=login`
          );
        });
      }
      return Promise.resolve();
    };

    handleResendVerificationEmail = () => {
      const { sendVerificationEmail } = this.props;

      return sendVerificationEmail({
        variables: {
          input: {
            emailAddress: user.get().email_address,
            userId: user.get().id,
          },
        },
      });
    };

    handleGoingHome = () => {
      redirect.setLocation(`/`);
    };

    componentDidMount() {
      const { router } = this.context;
      if (router.location.query.adminInvitationId) {
        this.handleInvite();
      }
    }

    render() {
      const { router } = this.context;
      const { loading, errorCode, errorMessage, didAccept } = this.state;

      if (!router.location.query.adminInvitationId) {
        // @ts-expect-error TS2559: Type '{ onAdminRolesUpdated: () => Promise<any>; sendVerificationEmail: (request: { variables: { input: SendVerificationEmailRequest; }; }) => Promise<FetchResult<{ ...; }>>; acceptAllInvitationsForEmailAndOrganization: (request: { ...; }) => Promise<...>; onInviteAccepted: () => void; }' has no properties in common with type 'IntrinsicAttributes'.
        return <Component {...this.props} />;
      }

      if (didAccept) {
        return <YouHaveBeenGrantedAdminPrivileges {...this.props} onContinue={this.handleContinueToCoursera} />;
      }

      if (errorCode === 'Not Authorized') {
        return <AuthenticationOption />;
      }

      if (errorCode === 'EMAIL_NOT_ASSOCIATED_WITH_USER') {
        return (
          <SwitchAccountForAdminAccess
            emailLoggedInWith={user.get().email_address}
            onLogout={this.handleLogout}
            onGoToCourseraHome={() => {
              redirect.setLocation(`/`);
            }}
          />
        );
      }

      if (errorCode === 'NO_ACTIVE_ENTERPRISE_ADMIN_INVITATION_FOUND') {
        return <InvitationIsNoLongerActive onGoToCourseraHome={this.handleGoingHome} />;
      }

      if (errorCode === 'EMAIL_IS_NOT_VERIFIED') {
        return (
          <EmailVerification
            email={user.get().email_address}
            onConfirm={this.handleInvite}
            onResend={this.handleResendVerificationEmail}
          />
        );
      }

      return (
        <section
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
          `}
        >
          <Spacer size={64} />
          {loading && <LoadingSection />}
          {errorCode && (
            <InlineNotification severity="warning">
              {_t('Unsupported error code: #{errorCode}', { errorCode })}
            </InlineNotification>
          )}
          {errorMessage && <InlineNotification severity="warning">{errorMessage}</InlineNotification>}
        </section>
      );
    }
  };
